import type {
  AuthState,
  LoginCredentials,
  LoginResponse,
  User,
} from "@repo/ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useApiWithLanguage } from "./useApiWithLanguage";

const authKeys = {
  session: ["auth", "session"] as const,
  user: ["auth", "user"] as const,
};

export function useAuth() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { fetchWithLanguage } = useApiWithLanguage();

  const { data: authState, isLoading } = useQuery<AuthState>({
    queryKey: authKeys.session,
    queryFn: async () => {
      const response = await fetchWithLanguage("/api/auth/session", {
        credentials: 'include'
      });
      
      if (!response.ok) {
        return {
          isAuthenticated: false,
          isLoading: false,
          user: null,
        };
      }
      
      const data = await response.json();
      
      const authState = {
        isAuthenticated: data.authenticated,
        isLoading: false,
        user: data.user || null,
      };
      
      return authState;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  });

  const login = useMutation<LoginResponse, Error, LoginCredentials>({
    mutationFn: async (credentials) => {
      const response = await fetchWithLanguage("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Inloggen mislukt");
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.session, {
        isAuthenticated: true,
        isLoading: false,
        user: data.user,
      });
      router.push("/booking");
    },
  });

  const logout = useMutation({
    mutationFn: async () => {
      await fetchWithLanguage("/api/auth/logout", { method: "POST" });
    },
    onSuccess: () => {
      queryClient.setQueryData(authKeys.session, {
        isAuthenticated: false,
        isLoading: false,
        user: null,
      });
      router.push("/login");
    },
    onError: () => {
      // Even if logout fails, clear local state
      queryClient.setQueryData(authKeys.session, {
        isAuthenticated: false,
        isLoading: false,
        user: null,
      });
      router.push("/login");
    },
  });

  const getCurrentUser = (): User | null => {
    return authState?.user || null;
  };

  const getUserInfo = (field?: keyof User) => {
    if (!authState?.user) return null;
    return field ? authState.user[field] : authState.user;
  };

  return {
    isAuthenticated: authState?.isAuthenticated || false,
    isLoading,
    user: authState?.user || null,
    login,
    logout,
    getCurrentUser,
    getUserInfo,
  };
}